import { observer } from "mobx-react-lite";
import { useEvent } from "src/hooks/useEvent";
import { TableContentType } from "src/components/ExplorePanel/TableContentType";
import { TableBody } from "./TableBody";
import { TableLoadingCell } from "./TableLoadingCell";
import { TableRow } from "./TableRow";
import type { RowData } from "./RowData";
import type { WorkspaceConfig } from "src/hooks/useWorkspaceQuery";
import { CURRENT_WORKSPACE_VERSION, WorkspaceQueryKey } from "src/hooks/useWorkspaceQuery";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "src/contexts/api.context";
import type { Editor } from "src/types/Editor";
import { useMetricsCalculationsQuery } from "src/hooks/useMetricsCalculationsQuery";
import { TableStore } from "./TableStore";
import { useWorldThumbnail } from "src/hooks/useWorldThumbnail";
import type {
  CarbonView,
  CostingView,
} from "src/service/cost/utility/custom-view";
import {
  createCarbonViewForTotal,
  createCostingViewForTotal,
} from "src/service/cost/utility/custom-view";
import { ColumnsGroup } from "./ColumnsGroup";
import type { ReactNode } from "react";
import { toJS } from "mobx";

interface Props {
  workspaceConfig: WorkspaceConfig;
  editor: Editor;
  table: TableStore;
}

export const Table = observer(function Table(props: Props) {
  const queryClient = useQueryClient();
  const cavnasRowData = useCanvasRowData(props.editor);
  const api = useApi();

  const putTableConfig = useEvent(async () => {
    const workspace: WorkspaceConfig = {
      ...props.workspaceConfig,
      explorePanel: toJS(props.table.config),
      version: CURRENT_WORKSPACE_VERSION,
    };
    queryClient.setQueryData(WorkspaceQueryKey, () => workspace);
    await api.PostWorkspaceLayout(workspace);
    queryClient.invalidateQueries(WorkspaceQueryKey);
  });

  if (!cavnasRowData) return null;

  return (
    <div className="overflow-auto w-full flex flex-col relative h-full">
      <div className="flex w-full">
        <PinColumns>
          <ColumnsGroup
            columns={props.table.pinnedColumns}
            table={props.table}
            canvasRowData={cavnasRowData}
            onConfigSubmit={putTableConfig}
          />
        </PinColumns>
        <ColumnsGroup
          columns={props.table.unpinnedColumns}
          table={props.table}
          canvasRowData={cavnasRowData}
          onConfigSubmit={putTableConfig}
        />
      </div>
      <LoadingRows table={props.table} />
      <EmptyState editor={props.editor} table={props.table} />
    </div>
  );
});

const PinColumns = observer(function PinColumns(props: { children: ReactNode }) {
  return (
    <div className="border-r border-neutral-4 sticky left-0 flex z-[11]">{props.children}</div>
  );
});

const LoadingRows = observer(function LoadingRows(props: { table: TableStore }) {
  return (
    <TableBody>
      {new Array(props.table.loadingRowsSize).fill(null).map((_, index) => {
        return (
          <TableRow key={index} type={TableContentType.Body} rowId={`${index}`}>
            <TableLoadingCell />
          </TableRow>
        );
      })}
    </TableBody>
  );
});

function useCanvasRowData(editor: Editor): RowData | null {
  const calculation = useMetricsCalculationsQuery(editor.projectId, editor.world);
  const thumbnail = useWorldThumbnail(editor.world) ?? "";
  if (!editor.world) return null;
  const costingView: CostingView = calculation.data
    ? createCostingViewForTotal(calculation.data)
    : {
        element: {
          modular: [],
          basebuild: [],
        },
        summary: {
          construction: 0,
          constructionBaseBuild: 0,
          constructionFitOut: 0,
          constructionModules: 0,
          contingencies: 0,
          overheadsAndProfits: 0,
          preliminaries: 0,
          total: 0,
        },
      };
  const sustainabilityView: CarbonView = calculation.data
    ? createCarbonViewForTotal(calculation.data)
    : { element: [], summary: { total: 0, substructure: 0, other: 0, superstructure: 0 } };
  if (!costingView || !sustainabilityView) return null;
  const row = TableStore.createRowData(
    -1,
    {
      architecturalMetrics: editor.world.architecturalMetrics,
      costMetrics: costingView,
      sustainabilityMetrics: sustainabilityView,
      thumbnail,
      rgWorldHashKey: editor.world.rgWorldHashKey,
      rgWorldZip: new Uint8Array(),
    },
    "Canvas"
  );
  return row;
}

const EmptyState = observer(function EmptyState(props: { editor: Editor; table: TableStore }) {
  if (props.editor.designGenerator.isRunning || props.table.finalData.length > 0) return null;
  return (
    <div className="space-y-5 justify-center items-center flex flex-col text-center p-7 h-full flex-1">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
      >
        <circle cx="32" cy="32" r="32" fill="#E5E3F2" />
        <path
          d="M18.7719 38.8404C17.1651 37.9547 15.598 37.0906 14.0065 36.2128C14.0065 34.552 14.0146 32.9209 14 31.2897C13.9968 30.9407 14.2138 30.8148 14.4576 30.6728C15.6814 29.9627 16.9035 29.251 18.1256 28.5386C19.4782 27.7499 20.8364 26.9692 22.1784 26.1629C22.542 25.9446 22.8311 25.9198 23.2175 26.1364C28.2396 28.9542 33.2691 31.7583 38.305 34.552C38.6889 34.7654 38.8404 34.9837 38.7934 35.441C38.6282 37.0641 38.4962 38.6912 38.3941 40.3191C38.3593 40.8784 38.1552 41.2113 37.6498 41.4978C35.1877 42.8922 32.7418 44.3147 30.3154 45.7693C29.7776 46.0919 29.3767 46.0911 28.8649 45.7557C27.978 45.1748 27.0669 44.6276 26.1347 44.1214C25.6828 43.8759 25.567 43.5774 25.5937 43.0936C25.6844 41.4889 25.7306 39.8827 25.7921 38.2764C25.7994 38.0782 25.7929 37.8793 25.7929 37.665C24.9693 37.2181 24.1634 36.7985 23.3778 36.3452C23.0798 36.1735 22.8627 36.2537 22.6125 36.4142C21.592 37.0689 20.5659 37.7156 19.5413 38.3639C19.3073 38.5123 19.0692 38.6543 18.7711 38.838L18.7719 38.8404ZM37.7389 35.0454C37.5259 34.909 37.4239 34.836 37.3161 34.7759C32.6131 32.1538 27.9076 29.5375 23.2118 26.9026C22.8158 26.6804 22.5307 26.7349 22.1695 26.9483C19.9714 28.2465 17.7612 29.5254 15.5567 30.814C15.4125 30.8982 15.2821 31.0049 15.074 31.1517C16.1859 31.7663 17.2275 32.3223 18.2471 32.9161C18.6229 33.1351 18.9202 33.1311 19.2951 32.9145C20.4112 32.271 21.5572 31.6781 22.6724 31.033C23.0458 30.8172 23.3292 30.8035 23.7147 31.0266C25.8804 32.2798 28.0647 33.501 30.2401 34.7373C30.3802 34.8168 30.5041 34.9267 30.6904 35.0623C29.3719 35.8847 28.123 36.6637 26.8053 37.4853C27.7796 38.0863 28.66 38.5869 29.4901 39.1598C29.9185 39.4558 30.2304 39.4189 30.6531 39.171C32.8099 37.9033 34.9845 36.6645 37.1517 35.4137C37.321 35.3158 37.483 35.2059 37.7389 35.0446V35.0454ZM38.0807 35.7194C37.913 35.7796 37.8385 35.7948 37.7754 35.8309C35.4339 37.1772 33.0982 38.534 30.7487 39.8666C30.3915 40.0696 30.2603 40.3296 30.2409 40.7083C30.1931 41.6358 30.1194 42.5608 30.0773 43.4883C30.0538 43.9978 30.0732 44.5089 30.0732 45.1179C32.6519 43.6151 35.1513 42.1589 37.6563 40.6994C37.798 39.0378 37.9365 37.4163 38.0815 35.7194H38.0807ZM23.3527 35.5726C24.1804 36.0227 24.962 36.4319 25.7265 36.8707C26.0294 37.0448 26.27 37.0344 26.5607 36.8459C27.3431 36.3388 28.1424 35.859 28.9321 35.3632C29.0665 35.2789 29.1856 35.169 29.38 35.0182C27.4014 33.9005 25.5079 32.831 23.5301 31.7142C23.4669 33.0757 23.411 34.2953 23.3519 35.5734L23.3527 35.5726ZM26.5016 38.0895C26.4174 39.9517 26.3396 41.667 26.2602 43.4041C27.2564 43.9986 28.2218 44.5755 29.3014 45.2205C29.4026 43.5004 29.5023 41.9061 29.5792 40.3111C29.5865 40.1675 29.4844 39.9557 29.3654 39.8787C28.4624 39.2946 27.5431 38.7361 26.5008 38.0903L26.5016 38.0895ZM14.6633 31.7021C14.6633 33.0613 14.6739 34.264 14.6569 35.4675C14.652 35.8213 14.8788 35.9376 15.1258 36.0732C16.0556 36.5827 16.9829 37.097 17.9151 37.6025C18.0609 37.6811 18.2269 37.7228 18.4488 37.8062C18.4488 36.5321 18.4593 35.3303 18.4366 34.1284C18.4334 33.9639 18.3095 33.732 18.1702 33.6518C17.059 33.0123 15.93 32.4018 14.6633 31.7029V31.7021ZM22.8239 31.8506C22.7008 31.8642 22.6157 31.8506 22.5558 31.8835C21.5126 32.4499 20.4776 33.03 19.4304 33.5892C19.1283 33.7505 19.0991 33.9904 19.1016 34.2808C19.1097 35.1594 19.1016 36.0387 19.1056 36.9173C19.1064 37.1796 19.1259 37.4412 19.1413 37.8215C20.3156 37.0777 21.3814 36.4118 22.4335 35.7258C22.559 35.644 22.6797 35.4546 22.687 35.3094C22.7477 34.1637 22.7809 33.0163 22.823 31.8514L22.8239 31.8506Z"
          fill="#5F588E"
        />
        <path
          d="M38.828 28.009C38.7721 26.126 38.7308 24.2421 38.6482 22.3598C38.6287 21.9258 38.7348 21.6714 39.1301 21.4556C40.7272 20.5843 42.3105 19.6873 43.889 18.7831C44.2381 18.5833 44.5224 18.5825 44.8706 18.7903C46.5406 19.7884 48.2163 20.7776 49.9065 21.742C50.2921 21.9619 50.4176 22.221 50.416 22.6479C50.4022 26.3418 50.4022 30.0357 50.416 33.7288C50.4176 34.1605 50.2913 34.4164 49.8855 34.6122C48.1799 35.437 46.4855 36.285 44.7977 37.1443C44.4365 37.3281 44.1466 37.3024 43.8072 37.1018C42.3931 36.2658 40.9734 35.4394 39.5415 34.6338C39.1633 34.4212 39.0224 34.1637 39.0134 33.7336C38.9738 31.8249 38.8976 29.917 38.8345 28.009C38.832 28.009 38.8304 28.009 38.828 28.009ZM44.2697 24.8334C45.8943 23.9388 47.4979 23.0555 49.1614 22.1392C48.9703 22.0052 48.8545 21.9121 48.7273 21.8375C47.4428 21.0833 46.1454 20.3492 44.8779 19.5685C44.4924 19.3311 44.213 19.3615 43.8437 19.5774C42.645 20.2778 41.4253 20.9429 40.217 21.6265C40.0501 21.7212 39.8987 21.8431 39.6848 21.9908C41.2479 22.96 42.7471 23.8891 44.2697 24.8334ZM49.6676 22.6735C49.3906 22.8075 49.1655 22.9054 48.9517 23.0218C48.0657 23.5048 47.1804 23.9894 46.3001 24.4828C45.7963 24.7652 45.3031 25.0645 44.805 25.3557C44.7807 25.9535 44.7459 26.552 44.7362 27.1497C44.7281 27.6624 44.7491 28.1751 44.7597 28.8322C46.488 27.916 48.081 27.0711 49.6676 26.2295V22.6735ZM39.4662 26.0866C40.9564 27.0013 42.4466 27.916 44.0137 28.8779V25.5026C42.1137 24.1715 39.8857 22.8565 39.3196 22.7474C39.3682 23.8554 39.4168 24.953 39.467 26.0866H39.4662ZM49.6531 33.9695V30.7811C48.0559 31.5312 46.5317 32.2405 45.0172 32.9698C44.8892 33.0316 44.7532 33.2306 44.7499 33.3694C44.7273 34.3402 44.7378 35.3126 44.7378 36.411C46.458 35.5565 48.0705 34.7558 49.6531 33.9695ZM44.0146 33.1985C42.7211 32.2918 40.153 30.8589 39.6913 30.765C39.6913 31.6556 39.6978 32.5558 39.6881 33.4568C39.684 33.8243 39.8412 34.0449 40.1659 34.2255C41.2237 34.812 42.2636 35.4305 43.3132 36.0323C43.5229 36.1526 43.7432 36.2545 44.0146 36.3949V33.1993V33.1985ZM44.7362 32.3488C46.4532 31.5401 48.0624 30.7827 49.6506 30.0349V27.0037C48.0381 27.8582 46.5042 28.6645 44.9824 29.4917C44.8593 29.5591 44.7516 29.7693 44.7467 29.9162C44.7232 30.6864 44.7362 31.4582 44.7362 32.3488ZM44.0048 29.665C42.5179 28.7544 41.0463 27.8542 39.5698 26.95C39.5698 27.8437 39.5788 28.6413 39.5658 29.438C39.5601 29.7765 39.778 29.9129 40.0258 30.055C40.8997 30.5572 41.7695 31.0667 42.641 31.5722C43.0702 31.8209 43.5019 32.0664 44.0048 32.3552V29.6658V29.665Z"
          fill="#5F588E"
        />
        <path
          d="M30.6864 18.0008C30.8929 18.1091 31.0759 18.1934 31.2468 18.2969C32.9354 19.3118 34.6168 20.3388 36.3119 21.3417C36.6998 21.5712 36.8958 21.8335 36.8715 22.3029C36.8343 23.029 36.8481 23.7591 36.8675 24.4868C36.8764 24.8326 36.7476 25.0492 36.4447 25.2113C34.5747 26.2142 32.7079 27.2219 30.8459 28.2393C30.5641 28.3933 30.3405 28.3629 30.0765 28.1984C28.4672 27.1995 26.8515 26.2102 25.2333 25.225C24.9742 25.0669 24.8316 24.8864 24.8381 24.563C24.8559 23.6339 24.8478 22.7048 24.8413 21.7758C24.8397 21.5078 24.8916 21.2968 25.154 21.1483C26.9997 20.1053 28.8398 19.0527 30.6864 18V18.0008ZM30.5236 24.6794C32.2956 23.7366 34.0296 22.8148 35.8373 21.8536C34.0847 20.7945 32.4058 19.7796 30.7115 18.7566C29.0358 19.713 27.3763 20.6597 25.6245 21.6586C27.2969 22.6896 28.8771 23.6644 30.5228 24.6794H30.5236ZM36.1005 24.6288V22.4786C34.4208 23.3716 32.8229 24.2148 31.2339 25.0757C31.1035 25.1463 30.9601 25.3277 30.9536 25.4632C30.9237 26.073 30.9407 26.6844 30.9407 27.4137C32.7411 26.4421 34.4176 25.5379 36.1013 24.6288H36.1005ZM30.249 27.5036C30.249 26.8248 30.2304 26.3057 30.2555 25.789C30.2733 25.4135 30.1162 25.2033 29.7971 25.0123C28.5191 24.2477 27.2589 23.4542 25.989 22.6752C25.8675 22.6005 25.7298 22.5532 25.5379 22.465V24.6168C27.0313 25.5322 28.5693 26.4742 30.249 27.5036Z"
          fill="#5F588E"
        />
        <path
          d="M37.7392 35.0463C37.4832 35.2075 37.3213 35.3174 37.152 35.4153C34.9847 36.6662 32.8101 37.905 30.6534 39.1726C30.2306 39.4206 29.9188 39.4575 29.4904 39.1614C28.661 38.5885 27.7799 38.0879 26.8056 37.4869C28.1233 36.6654 29.3721 35.8863 30.6906 35.0639C30.5044 34.9283 30.3813 34.8184 30.2403 34.739C28.065 33.5034 25.8807 32.2814 23.715 31.0282C23.3295 30.8052 23.046 30.8188 22.6727 31.0346C21.5574 31.6797 20.4114 32.2726 19.2954 32.9161C18.9204 33.1327 18.6224 33.1359 18.2474 32.9177C17.2277 32.324 16.1862 31.768 15.0742 31.1534C15.2824 31.0073 15.4128 30.8998 15.5569 30.8156C17.7615 29.5279 19.9717 28.2489 22.1697 26.95C22.5309 26.7366 22.816 26.682 23.2121 26.9042C27.9078 29.5391 32.6133 32.1555 37.3164 34.7775C37.4249 34.8377 37.5262 34.9107 37.7392 35.0471V35.0463Z"
          fill="white"
        />
        <path
          d="M38.0803 35.7202C37.9353 37.4171 37.7968 39.0386 37.6551 40.7003C35.1501 42.1597 32.6507 43.6159 30.072 45.1187C30.072 44.5097 30.0526 43.9978 30.0761 43.4891C30.1182 42.5617 30.1919 41.6358 30.2397 40.7091C30.2591 40.3312 30.3903 40.0704 30.7475 39.8674C33.097 38.5348 35.4327 37.1788 37.7741 35.8317C37.8373 35.7956 37.9118 35.7804 38.0795 35.7202H38.0803Z"
          fill="white"
        />
        <path
          d="M23.3521 35.5734C23.4112 34.2953 23.4671 33.0757 23.5302 31.7142C25.5072 32.831 27.4007 33.9005 29.3801 35.0182C29.1849 35.1698 29.0667 35.2789 28.9322 35.3632C28.1426 35.859 27.3424 36.3396 26.5609 36.8459C26.2701 37.0344 26.0304 37.0448 25.7267 36.8707C24.9621 36.4319 24.1806 36.0227 23.3529 35.5726L23.3521 35.5734Z"
          fill="white"
        />
        <path
          d="M26.5003 38.0903C27.5426 38.7362 28.4619 39.2946 29.3649 39.8787C29.484 39.9557 29.5852 40.1675 29.5787 40.3111C29.5018 41.9061 29.4014 43.5004 29.3009 45.2206C28.2213 44.5763 27.2559 43.9994 26.2598 43.4041C26.3383 41.6671 26.4161 39.9525 26.5011 38.0895L26.5003 38.0903Z"
          fill="white"
        />
        <path
          d="M14.6628 31.7022C15.9295 32.401 17.0585 33.0116 18.1697 33.651C18.309 33.7312 18.4329 33.9639 18.4361 34.1276C18.4588 35.3295 18.4483 36.5314 18.4483 37.8055C18.2263 37.722 18.0603 37.6811 17.9145 37.6017C16.9823 37.0962 16.055 36.5811 15.1253 36.0724C14.879 35.9376 14.6515 35.8213 14.6563 35.4667C14.6733 34.264 14.6628 33.0605 14.6628 31.7014V31.7022Z"
          fill="white"
        />
        <path
          d="M22.8232 31.8506C22.781 33.0156 22.7478 34.1629 22.6871 35.3086C22.679 35.4538 22.5591 35.6432 22.4336 35.725C21.3815 36.411 20.3157 37.0778 19.1414 37.8207C19.126 37.4412 19.1066 37.1788 19.1057 36.9165C19.1017 36.0379 19.1098 35.1586 19.1017 34.28C19.0993 33.9896 19.1284 33.7497 19.4305 33.5884C20.4777 33.0292 21.5128 32.4499 22.5559 31.8827C22.6158 31.8498 22.7009 31.8634 22.824 31.8498L22.8232 31.8506Z"
          fill="white"
        />
        <path
          d="M44.2694 24.8334C42.7468 23.8891 41.2477 22.96 39.6846 21.9908C39.8984 21.8431 40.049 21.7212 40.2167 21.6265C41.425 20.9429 42.6447 20.2778 43.8434 19.5774C44.2127 19.3615 44.4921 19.3311 44.8776 19.5685C46.1459 20.3492 47.4426 21.0825 48.7271 21.8375C48.8542 21.9121 48.97 22.006 49.1612 22.1392C47.4976 23.0555 45.894 23.9388 44.2694 24.8334Z"
          fill="white"
        />
        <path
          d="M49.6681 22.6744V26.2303C48.0815 27.0711 46.4885 27.916 44.7602 28.833C44.7496 28.1767 44.7286 27.6632 44.7367 27.1506C44.7464 26.552 44.7812 25.9543 44.8055 25.3566C45.3036 25.0645 45.7968 24.7652 46.3006 24.4836C47.1809 23.9902 48.0662 23.5064 48.9522 23.0226C49.166 22.9063 49.3912 22.8084 49.6681 22.6744Z"
          fill="white"
        />
        <path
          d="M39.4662 26.0867C39.416 24.953 39.3683 23.8554 39.3188 22.7474C39.885 22.8565 42.113 24.1715 44.013 25.5026V28.878C42.4459 27.916 40.9565 27.0021 39.4654 26.0867H39.4662Z"
          fill="white"
        />
        <path
          d="M49.654 33.9703C48.0715 34.7566 46.459 35.5573 44.7388 36.4118C44.7388 35.3134 44.7282 34.3418 44.7509 33.3702C44.7542 33.2322 44.8902 33.0324 45.0182 32.9706C46.5319 32.2405 48.0561 31.532 49.654 30.7819V33.9703Z"
          fill="white"
        />
        <path
          d="M44.0155 33.1985V36.3942C43.7434 36.2545 43.5231 36.1518 43.3141 36.0315C42.2645 35.4298 41.2246 34.8112 40.1669 34.2247C39.8421 34.0441 39.685 33.8235 39.689 33.456C39.6988 32.5558 39.6923 31.6548 39.6923 30.7642C40.1539 30.8581 42.7221 32.2911 44.0155 33.1977V33.1985Z"
          fill="white"
        />
        <path
          d="M44.7371 32.3488C44.7371 31.4582 44.725 30.6864 44.7477 29.9162C44.7517 29.7693 44.8602 29.5583 44.9833 29.4917C46.5051 28.6645 48.0391 27.8582 49.6516 27.0037V30.0349C48.0626 30.7827 46.4541 31.5409 44.7371 32.3488Z"
          fill="white"
        />
        <path
          d="M44.0056 29.665V32.3544C43.5026 32.0656 43.0709 31.8209 42.6417 31.5714C41.7694 31.0659 40.9004 30.5564 40.0265 30.0542C39.7787 29.9122 39.5608 29.7758 39.5665 29.4372C39.5795 28.6405 39.5706 27.843 39.5706 26.9492C41.0478 27.8534 42.5186 28.7544 44.0056 29.6642V29.665Z"
          fill="white"
        />
        <path
          d="M30.5233 24.6802C28.8767 23.6652 27.2974 22.6904 25.625 21.6594C27.376 20.6605 29.0363 19.713 30.712 18.7574C32.4055 19.7812 34.0844 20.7953 35.8378 21.8544C34.0301 22.8156 32.2969 23.7374 30.5241 24.6802H30.5233Z"
          fill="white"
        />
        <path
          d="M36.1005 24.6296C34.4167 25.5379 32.7403 26.4429 30.9399 27.4145C30.9399 26.6852 30.9229 26.0738 30.9528 25.4641C30.9593 25.3277 31.1035 25.1471 31.233 25.0765C32.8221 24.2156 34.42 23.3724 36.0997 22.4794V24.6296H36.1005Z"
          fill="white"
        />
        <path
          d="M30.2488 27.5044C28.5698 26.4758 27.031 25.5331 25.5376 24.6176V22.4658C25.7295 22.554 25.868 22.6014 25.9887 22.676C27.2578 23.455 28.518 24.2485 29.7968 25.0131C30.1159 25.2041 30.2731 25.4143 30.2552 25.7898C30.2301 26.3065 30.2488 26.8256 30.2488 27.5044Z"
          fill="white"
        />
      </svg>
      <div className="text-subtitle-2 text-neutral-8">
        There are no design options available
        <br />
        based on zone and brief inputs
      </div>
    </div>
  );
});
